import { AlertCircle } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

export interface CDEditableTextProps {
  /** The text value to display */
  value: string | null | undefined;
  /** Whether this is an editable field */
  editable?: boolean;
  /** onChange handler for editable mode */
  onChange?: (value: string) => void;
  /** onBlur handler for validation */
  onBlur?: () => void;
  /** Placeholder text for empty values */
  placeholder?: string;
  /** Size variant */
  size?: 'tiny' | 'small' | 'body';
  /** Whether the text should be bold */
  bold?: boolean;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
  /** Input type (text, email, tel, etc.) */
  type?: 'text' | 'email' | 'tel';
  /** Validation error message */
  error?: string;
  /** Whether field is required */
  required?: boolean;
  /** Unique ID for accessibility */
  id?: string;
  /** Additional className */
  className?: string;
}

/**
 * CDEditableText - Editable text field component
 *
 * Displays text that can be edited when in editable mode.
 * Matches CD form styling seamlessly.
 */
export const CDEditableText = React.forwardRef<HTMLSpanElement, CDEditableTextProps>(
  (
    {
      value,
      editable = false,
      onChange,
      onBlur,
      placeholder = '',
      size = 'tiny',
      bold = false,
      align = 'left',
      type = 'text',
      error,
      required,
      id,
      className,
    },
    ref,
  ) => {
    const displayValue = value || placeholder;
    const hasError = !!error;
    const errorId = id ? `${id}-error` : undefined;

    const sizeClass = {
      tiny: 'cd-text-tiny',
      small: 'cd-text-small',
      body: 'cd-text-body',
    }[size];

    const alignClass = {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    }[align];

    if (editable) {
      return (
        <div className="relative">
          <input
            id={id}
            type={type}
            value={value || ''}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
            placeholder={placeholder}
            required={required}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
            className={cn(
              'cd-input w-full',
              sizeClass,
              alignClass,
              bold && 'font-bold',
              hasError && 'border-red-500 focus:border-red-500 focus:ring-red-500',
              className,
            )}
          />
          {hasError && (
            <div id={errorId} className="flex items-center gap-1 mt-0.5 text-red-600" role="alert">
              <AlertCircle className="h-3 w-3 flex-shrink-0" />
              <span className="text-[9px]">{error}</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <span
        ref={ref}
        className={cn(
          sizeClass,
          alignClass,
          bold && 'font-bold',
          !value && 'text-cd-gray-text',
          className,
        )}
      >
        {displayValue}
      </span>
    );
  },
);

CDEditableText.displayName = 'CDEditableText';
